/**
 * ConnectClaudeModal — interactive `claude setup-token` flow for
 * claude_code workspaces.
 *
 * Flow:
 *   1. POST /api/v1/credentials/:instanceId/connect → cloud queues an
 *      authenticate_provider command with bot_type=claude_code.
 *   2. Agent spawns `claude setup-token` under PTY with CLAUDE_CONFIG_DIR
 *      pointed at the per-instance dir.
 *   3. PTY output streams back as log_stream → SSE.
 *   4. We scrape any https://… URL out of the byte stream and render
 *      a clickable button so the user can open it in a new tab.
 *   5. User logs into claude.ai, copies the resulting code.
 *   6. User pastes it into our composer; we POST /agents/:id/logs/:commandId/stdin
 *      (base64-encoded + newline) so the agent forwards to the PTY's stdin.
 *   7. claude finishes writing .credentials.json and exits; the stream ends.
 *
 * Decoding: log_stream lines are base64-encoded raw PTY bytes (the
 * same shape used by TerminalSession). We decode, strip ANSI for the
 * URL scrape, but keep raw bytes for the optional debug view.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, getAuthHeaders } from '../services/api';
import { useApiHeaders } from '../hooks/useApiHeaders';

interface ConnectClaudeModalProps {
  instanceId: string;
  agentId: string;
  onClose: () => void;
  onConnected: () => void;
}

// Strip ANSI escape sequences so the URL we scrape is clean text.
function stripAnsi(s: string): string {
  // eslint-disable-next-line no-control-regex
  return s.replace(/\x1B\[[0-?]*[ -/]*[@-~]/g, '');
}

function extractUrl(text: string): string | null {
  const m = text.match(/https?:\/\/[^\s"'<>`]+/);
  return m ? m[0] : null;
}

export default function ConnectClaudeModal({ instanceId, agentId, onClose, onConnected }: ConnectClaudeModalProps) {
  const { firebaseUser } = useAuth();
  const { getHeaders } = useApiHeaders();
  const [commandId, setCommandId] = useState<string | null>(null);
  const [accumulated, setAccumulated] = useState<string>('');
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState<'starting' | 'awaiting_url' | 'awaiting_code' | 'sending' | 'done' | 'error'>('starting');
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Kick off the flow.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await getHeaders();
        const res = await fetch(`${API_URL}/api/v1/credentials/${instanceId}/connect`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({ provider: 'anthropic' }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { error?: string };
          throw new Error(body.error || `connect failed: ${res.status}`);
        }
        const data = await res.json() as { command_id: string };
        if (cancelled) return;
        setCommandId(data.command_id);
        setStatus('awaiting_url');
      } catch (e: any) {
        if (!cancelled) {
          setError(String(e?.message ?? e));
          setStatus('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [instanceId, getHeaders]);

  // Subscribe to log_stream SSE for this command_id.
  useEffect(() => {
    if (!commandId || !firebaseUser) return;
    const controller = new AbortController();
    abortRef.current = controller;
    (async () => {
      try {
        const idToken = await firebaseUser.getIdToken();
        const res = await fetch(`${API_URL}/api/v1/agents/${agentId}/logs/${commandId}`, {
          headers: getAuthHeaders(idToken),
          signal: controller.signal,
        });
        if (!res.ok || !res.body) return;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const parts = buf.split('\n\n');
          buf = parts.pop() ?? '';
          for (const part of parts) {
            for (const line of part.split('\n')) {
              if (!line.startsWith('data: ')) continue;
              try {
                const log = JSON.parse(line.slice(6)) as { stream?: string; line?: string };
                if (!log.line) continue;
                // PTY lines arrive base64-encoded.
                let chunk = '';
                try { chunk = atob(log.line); } catch { chunk = log.line; }
                const clean = stripAnsi(chunk);
                setAccumulated(prev => {
                  const next = prev + clean;
                  if (!authUrl) {
                    const url = extractUrl(next);
                    if (url) {
                      setAuthUrl(url);
                      setStatus('awaiting_code');
                    }
                  }
                  // Setup-token complete marker (we emit this from the agent).
                  if (next.includes('[auth] setup-token complete')) {
                    setStatus('done');
                  }
                  return next;
                });
              } catch {
                /* malformed line, skip */
              }
            }
          }
        }
      } catch (e: any) {
        if (e?.name === 'AbortError') return;
        // Stream errors are non-fatal — the subprocess may still complete.
      }
    })();
    return () => controller.abort();
  }, [commandId, agentId, firebaseUser, authUrl]);

  // Trigger onConnected once we hit done.
  useEffect(() => {
    if (status === 'done') {
      const t = setTimeout(() => onConnected(), 500);
      return () => clearTimeout(t);
    }
  }, [status, onConnected]);

  const submitCode = useCallback(async () => {
    if (!commandId || !firebaseUser || !code.trim()) return;
    setSending(true);
    setStatus('sending');
    try {
      const idToken = await firebaseUser.getIdToken();
      // The PTY expects the code followed by a newline as if the user typed it.
      const payload = code.trim() + '\n';
      const encoded = btoa(payload);
      const res = await fetch(`${API_URL}/api/v1/agents/${agentId}/logs/${commandId}/stdin`, {
        method: 'POST',
        headers: getAuthHeaders(idToken),
        body: JSON.stringify({ line: encoded }),
      });
      if (!res.ok) throw new Error(`stdin push failed: ${res.status}`);
    } catch (e: any) {
      setError(String(e?.message ?? e));
      setStatus('error');
    } finally {
      setSending(false);
    }
  }, [agentId, code, commandId, firebaseUser]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => status !== 'sending' && onClose()} />
      <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6">
        <div className="flex items-start justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Connect Anthropic</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 cursor-pointer">✕</button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {status === 'starting' && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Starting authentication flow on your agent…</div>
        )}

        {(status === 'awaiting_url' || status === 'awaiting_code') && (
          <div className="space-y-4">
            <div>
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Step 1 — Open the authentication URL</div>
              {authUrl ? (
                <a
                  href={authUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-4 py-2 text-sm font-medium rounded-xl bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white"
                >
                  Open in new tab ↗
                </a>
              ) : (
                <div className="text-sm text-slate-400 dark:text-slate-600">Waiting for authentication URL…</div>
              )}
              {authUrl && (
                <div className="mt-2 text-[11px] font-mono text-slate-400 dark:text-slate-600 break-all">{authUrl}</div>
              )}
            </div>
            <div>
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Step 2 — Paste the code from claude.ai</div>
              <textarea
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Paste the authentication code here…"
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-mono"
                disabled={sending || !authUrl}
                spellCheck={false}
              />
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={() => void submitCode()}
                  disabled={!authUrl || !code.trim() || sending}
                  className="px-4 py-1.5 text-sm rounded-xl text-white bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 disabled:from-slate-400 disabled:to-slate-500 disabled:cursor-not-allowed cursor-pointer"
                >
                  {sending ? 'Sending…' : 'Submit code'}
                </button>
              </div>
            </div>
          </div>
        )}

        {status === 'sending' && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Sending code to agent…</div>
        )}

        {status === 'done' && (
          <div className="p-3 rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-900/20 text-sm text-emerald-700 dark:text-emerald-400">
            ✓ Authentication complete. Credentials saved.
          </div>
        )}

        {/* Debug: raw PTY tail (kept compact). Helps when the URL scrape misses. */}
        {accumulated && status !== 'done' && (
          <details className="mt-4">
            <summary className="text-[11px] text-slate-400 dark:text-slate-600 cursor-pointer">Show raw output</summary>
            <pre className="mt-1 text-[10px] font-mono text-slate-500 dark:text-slate-500 max-h-32 overflow-y-auto whitespace-pre-wrap">{accumulated.slice(-1200)}</pre>
          </details>
        )}
      </div>
    </div>
  );
}
